import { FormInstance, UploadFile } from 'antd';
import { BaseOptionType } from 'antd/es/select';
import { isDefined } from 'class-validator';
import { MutationType } from 'src/enums/operation.enum';

/* Submits the form. If the modal parameter is given, it closes the modal. */
export const submitForm = (form: FormInstance, modal?: { destroy: () => void }) => {
  form
    .validateFields()
    .then(() => {
      form.submit();
      if (modal) modal.destroy();
    })
    .catch((error) => {
      console.log('error', error);
    });
};

/*
It converts data consisting of id and name fields into select box options. 
As an extra, it takes the labelField parameter to use instead of the name field.
*/
export const convertToSelectOptionsWithId = <T extends { id: string; name?: string }>(
  data: T[],
  labelField?: keyof T,
  title?: keyof T,
): BaseOptionType[] => {
  return data.map((obj) => {
    return {
      value: obj.id,
      label: labelField ? obj[labelField] : obj.name,
      title: title ? obj[title]?.toString() : undefined,
    };
  });
};

type DeleteRecordConfigType = {
  modal: { destroy: () => void };
  recordId: string;
  deleteFn: (recordId: string) => void;
};

// Calls delete function provided for given record from table then closes the modal
export const deleteRecordFromTable = (config: DeleteRecordConfigType) => {
  config.deleteFn(config.recordId);
  config.modal.destroy();
};

export const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

/*
It creates form data from given object.
It takes fileFields parameter to specify field that keeps file data
It also takes mutationType parameter to determine which type of mutation to perform (create or update)
In create operation it does not send empty string values to the form data
*/
export const getFormData = <T extends { [k: string]: any }>(
  object: T,
  fileFields?: (keyof T)[],
  mutationType?: MutationType,
) => {
  return Object.keys(object).reduce((formData, key) => {
    if (fileFields && fileFields.includes(key) && !!object[key]) {
      if (Array.isArray(object[key])) {
        object[key]?.forEach((file: UploadFile) => {
          if (file?.originFileObj) formData.append(key, file.originFileObj);
        });
      } else {
        formData.append(key, object[key]?.originFileObj);
      }
    } else if (Array.isArray(object[key])) {
      if (!object[key].length && mutationType !== MutationType.CREATE)
        formData.append(key, JSON.stringify(object[key]));
      else {
        const isPrimitiveArray = object[key].every(
          (item) => item === null || ['string', 'number', 'boolean'].includes(typeof item),
        );

        if (isPrimitiveArray) {
          object[key]?.forEach((value: any) => {
            formData.append(key, value);
          });
        } else {
          formData.append(key, JSON.stringify(object[key]));
        }
      }
    } else if (mutationType === MutationType.CREATE) {
      if (isDefined(object[key]) && object[key] !== '') {
        formData.append(key, object[key]);
      }
    } else if (isDefined(object[key])) {
      formData.append(key, object[key]);
    }
    return formData;
  }, new FormData());
};
